import { useState, useRef, useEffect, useCallback } from 'react';
import JSZip from 'jszip';
import heic2any from 'heic2any';

// Constants at module scope
const MIN_WIDTH = 1080;
const MIN_HEIGHT = 1920;
const MAX_PREVIEW_HEIGHT = 300;
const DEFAULT_MESSAGE_DURATION = 5000;
const NATIVELY_SUPPORTED_EXTENSIONS = ['.png', '.webp', '.avif', '.jpeg', '.jpg', '.gif', '.bmp'];

export default function useImageConverter() {
  // Refs
  const fileInputIndividualRef = useRef(null);
  const folderInputRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const messageTimerRef = useRef(null);

  // Core state
  const [currentMode, setCurrentMode] = useState('individual');
  const [filesToConvert, setFilesToConvert] = useState([]);
  const [currentPreviewFile, setCurrentPreviewFile] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [conversionProgress, setConversionProgress] = useState({ current: 0, total: 0, fileName: '' });
  const [message, setMessage] = useState({ text: '', type: 'info', visible: false });
  const [results, setResults] = useState([]);
  const [showDownloadAll, setShowDownloadAll] = useState(false);
  const [showClearResults, setShowClearResults] = useState(false);
  const [fileInputIndividualText, setFileInputIndividualText] = useState('Drag & drop or click to select a PNG, HEIC, WebP, or AVIF file');
  const [folderInputText, setFolderInputText] = useState('Drag & drop or click to select a folder with image files');

  // Conversion options state
  const [outputFormat, setOutputFormat] = useState('jpeg');
  const [jpegQualityValue, setJpegQualityValue] = useState('0.90');
  const [filtersEnabled, setFiltersEnabled] = useState(false);
  const [removeBackground, setRemoveBackground] = useState(false);
  const [backgroundTolerance, setBackgroundTolerance] = useState(20);
  const [cropEnabled, setCropEnabled] = useState(false);
  const [cropSides, setCropSides] = useState({ top: 0, right: 0, bottom: 0, left: 0 });
  const [autoScale, setAutoScale] = useState(false);
  const [scaleFactor, setScaleFactor] = useState(1);

  // Consolidated custom filter state
  const [filters, setFilters] = useState({
    brightness: 100, contrast: 100, saturation: 100,
    grayscale: 0, sepia: 0, blur: 0, hueRotate: 0,
    invert: 0, opacity: 100
  });

  const updateCropSide = useCallback((side, value) => {
    setCropSides(prev => ({ ...prev, [side]: value }));
  }, []);

  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({ brightness: 100, contrast: 100, saturation: 100, grayscale: 0, sepia: 0, blur: 0, hueRotate: 0, invert: 0, opacity: 100 });
  }, []);

  // Message with auto-dismiss and cleanup
  const showUserMessage = useCallback((text, type = 'info', duration = DEFAULT_MESSAGE_DURATION) => {
    if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
    setMessage({ text, type, visible: true });
    if (duration > 0) {
      messageTimerRef.current = setTimeout(() => {
        setMessage(prev => prev.text === text ? { ...prev, visible: false } : prev);
      }, duration);
    }
  }, []);

  // Cleanup message timer on unmount
  useEffect(() => {
    return () => {
      if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
    };
  }, []);

  const getCurrentFilterValue = useCallback(() => {
    if (!filtersEnabled) return 'none';
    const parts = [
      filters.brightness !== 100 && `brightness(${filters.brightness / 100})`,
      filters.contrast !== 100 && `contrast(${filters.contrast / 100})`,
      filters.saturation !== 100 && `saturate(${filters.saturation / 100})`,
      filters.grayscale > 0 && `grayscale(${filters.grayscale / 100})`,
      filters.sepia > 0 && `sepia(${filters.sepia / 100})`,
      filters.blur > 0 && `blur(${filters.blur}px)`,
      filters.hueRotate > 0 && `hue-rotate(${filters.hueRotate}deg)`,
      filters.invert > 0 && `invert(${filters.invert / 100})`,
      filters.opacity !== 100 && `opacity(${filters.opacity / 100})`
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : 'none';
  }, [filtersEnabled, filters]);

  const isSupportedExtension = useCallback((fileName) => {
    const n = fileName.toLowerCase();
    return NATIVELY_SUPPORTED_EXTENSIONS.some(ext => n.endsWith(ext)) || n.endsWith('.heic') || n.endsWith('.heif');
  }, []);

  const removeWhiteBackgroundFromCanvas = useCallback((canvas, tolerance) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    try {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const threshold = 255 - tolerance;
      for (let i = 0; i < data.length; i += 4) {
        if (data[i] >= threshold && data[i + 1] >= threshold && data[i + 2] >= threshold) {
          data[i + 3] = 0;
        }
      }
      ctx.putImageData(imageData, 0, 0);
    } catch (error) {
      showUserMessage("Background removal failed (maybe CORS issue?).", "error");
    }
  }, [showUserMessage]);

  const getTrimmedCanvas = useCallback((canvas) => {
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    let minX = canvas.width, minY = canvas.height, maxX = 0, maxY = 0;
    let foundPixel = false;

    for (let y = 0; y < canvas.height; y++) {
      for (let x = 0; x < canvas.width; x++) {
        const alpha = data[((y * canvas.width + x) * 4) + 3];
        if (alpha > 0) {
          foundPixel = true;
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (!foundPixel) return canvas;

    const trimmedWidth = maxX - minX + 1;
    const trimmedHeight = maxY - minY + 1;
    const trimmedCanvas = document.createElement('canvas');
    trimmedCanvas.width = trimmedWidth;
    trimmedCanvas.height = trimmedHeight;
    trimmedCanvas.getContext('2d').drawImage(canvas, minX, minY, trimmedWidth, trimmedHeight, 0, 0, trimmedWidth, trimmedHeight);
    return trimmedCanvas;
  }, []);

  const loadImageFromFile = useCallback((file) => {
    return new Promise(async (resolve, reject) => {
      const fileNameLower = file.name.toLowerCase();
      if (fileNameLower.endsWith('.heic') || fileNameLower.endsWith('.heif')) {
        try {
          const result = await heic2any({ blob: file, toType: "image/png" });
          const blob = result instanceof Blob ? result : result[0];
          const url = URL.createObjectURL(blob);
          resolve({ url, isObjectURL: true });
        } catch (err) {
          reject(new Error(`HEIC error: ${err.message}`));
        }
      } else if (NATIVELY_SUPPORTED_EXTENSIONS.some(ext => fileNameLower.endsWith(ext))) {
        const reader = new FileReader();
        reader.onload = (e) => resolve({ url: e.target.result, isObjectURL: false });
        reader.onerror = () => reject(new Error(`Read failed: ${file.name}`));
        reader.readAsDataURL(file);
      } else {
        reject(new Error(`Unsupported type: ${file.name}`));
      }
    });
  }, []);

  const processCanvas = useCallback((img, options) => {
    const { filterValue, doRemoveBackground, tolerance, format, doCrop, cropValues, doAutoScale, downscaleFactor, enforceMinSize = false } = options;

    let canvas = document.createElement('canvas');
    let ctx = canvas.getContext('2d');

    let w = Math.round(img.width * downscaleFactor);
    let h = Math.round(img.height * downscaleFactor);

    if (enforceMinSize && downscaleFactor === 1) {
      const scaleW = w < MIN_WIDTH ? MIN_WIDTH / w : 1;
      const scaleH = h < MIN_HEIGHT ? MIN_HEIGHT / h : 1;
      const finalScale = Math.max(scaleW, scaleH);
      if (finalScale > 1) { w = Math.round(w * finalScale); h = Math.round(h * finalScale); }
    }

    canvas.width = w;
    canvas.height = h;

    const opaqueFormats = ['jpeg', 'bmp', 'ico'];
    if (opaqueFormats.includes(format) && !doRemoveBackground) {
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, w, h);
    }

    if (filterValue && filterValue !== 'none') ctx.filter = filterValue;
    ctx.drawImage(img, 0, 0, w, h);
    ctx.filter = 'none';

    if (doRemoveBackground) removeWhiteBackgroundFromCanvas(canvas, tolerance);

    if (doCrop && cropValues) {
      const t = Math.round(cropValues.top * downscaleFactor);
      const r = Math.round(cropValues.right * downscaleFactor);
      const b = Math.round(cropValues.bottom * downscaleFactor);
      const l = Math.round(cropValues.left * downscaleFactor);
      const newW = canvas.width - l - r;
      const newH = canvas.height - t - b;
      if (newW > 0 && newH > 0) {
        const cropped = document.createElement('canvas');
        cropped.width = newW;
        cropped.height = newH;
        cropped.getContext('2d').drawImage(canvas, l, t, newW, newH, 0, 0, newW, newH);
        canvas = cropped;
      }
    }

    if (doAutoScale) {
      const trimmed = getTrimmedCanvas(canvas);
      if (trimmed.width > 0 && trimmed.height > 0) canvas = trimmed;
    }

    return canvas;
  }, [removeWhiteBackgroundFromCanvas, getTrimmedCanvas]);

  const updatePreview = useCallback(async (file) => {
    if (!file) { setPreviewVisible(false); setCurrentPreviewFile(null); return; }
    setCurrentPreviewFile(file);
    setPreviewVisible(true);
    const canvas = previewCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    try {
      const { url, isObjectURL } = await loadImageFromFile(file);
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        const processed = processCanvas(img, {
          filterValue: getCurrentFilterValue(),
          doRemoveBackground: removeBackground,
          tolerance: backgroundTolerance,
          format: outputFormat,
          doCrop: cropEnabled,
          cropValues: cropSides,
          doAutoScale: autoScale,
          downscaleFactor: scaleFactor
        });

        const maxW = canvas.parentElement?.clientWidth || 300;
        const maxH = MAX_PREVIEW_HEIGHT;
        let dw = processed.width, dh = processed.height;
        const ratio = processed.width / processed.height;
        if (dw > maxW) { dw = maxW; dh = dw / ratio; }
        if (dh > maxH) { dh = maxH; dw = dh * ratio; }

        canvas.width = dw;
        canvas.height = dh;
        ctx.clearRect(0, 0, dw, dh);
        ctx.drawImage(processed, 0, 0, dw, dh);

        if (removeBackground) {
          canvas.style.backgroundImage = `linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)`;
          canvas.style.backgroundSize = '20px 20px';
          canvas.style.backgroundPosition = '0 0, 0 10px, 10px -10px, -10px 0px';
        } else {
          canvas.style.backgroundImage = 'none';
        }

        if (isObjectURL) URL.revokeObjectURL(url);
      };
      img.onerror = () => {
        if (isObjectURL) URL.revokeObjectURL(url);
        showUserMessage('Error loading preview.', 'error');
        setPreviewVisible(false);
      };
      img.src = url;
    } catch (err) {
      showUserMessage(err.message, 'error');
      setPreviewVisible(false);
    }
  }, [loadImageFromFile, processCanvas, getCurrentFilterValue, removeBackground, backgroundTolerance, outputFormat, cropEnabled, cropSides, autoScale, scaleFactor, showUserMessage]);

  // Debounced preview update when options change (150ms debounce)
  useEffect(() => {
    if (!currentPreviewFile) return;
    const timer = setTimeout(() => updatePreview(currentPreviewFile), 150);
    return () => clearTimeout(timer);
  }, [currentPreviewFile, filtersEnabled, filters, removeBackground, backgroundTolerance, cropEnabled, cropSides, autoScale, scaleFactor, updatePreview]);

  const clearConverterOutputs = useCallback(() => {
    setResults([]);
    setShowDownloadAll(false);
    setShowClearResults(false);
    if (fileInputIndividualRef.current) fileInputIndividualRef.current.value = '';
    if (folderInputRef.current) folderInputRef.current.value = '';
    setFileInputIndividualText('Drag & drop or click to select a file');
    setFolderInputText('Drag & drop or click to select a folder');
    setFilesToConvert([]);
    setCurrentPreviewFile(null);
    setPreviewVisible(false);
    if (previewCanvasRef.current) {
      previewCanvasRef.current.getContext('2d').clearRect(0, 0, previewCanvasRef.current.width, previewCanvasRef.current.height);
    }
    setFiltersEnabled(false);
    resetFilters();
  }, [resetFilters]);

  const processAndConvertImage = useCallback(async (file) => {
    const { url, isObjectURL } = await loadImageFromFile(file);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.onload = () => {
        const canvas = processCanvas(img, {
          filterValue: getCurrentFilterValue(),
          doRemoveBackground: removeBackground,
          tolerance: backgroundTolerance,
          format: outputFormat,
          doCrop: cropEnabled,
          cropValues: cropSides,
          doAutoScale: autoScale,
          downscaleFactor: scaleFactor,
          enforceMinSize: true
        });

        if (isObjectURL) URL.revokeObjectURL(url);

        const formatMap = {
          jpeg: 'image/jpeg',
          png: 'image/png',
          webp: 'image/webp',
          gif: 'image/gif',
          bmp: 'image/bmp',
          ico: 'image/x-icon'
        };
        const mimeType = formatMap[outputFormat] || 'image/jpeg';
        const quality = parseFloat(jpegQualityValue);
        const dataURL = canvas.toDataURL(mimeType, quality);
        const originalName = file.name;
        const ext = outputFormat === 'jpeg' ? 'jpg' : outputFormat;
        const newName = originalName.substring(0, originalName.lastIndexOf('.')) + `.${ext}`;

        // File size calculation
        const originalSize = file.size;
        const outputSize = Math.round((dataURL.length - dataURL.indexOf(',') - 1) * 3 / 4);

        resolve({ originalName, newName, dataURL, originalSize, outputSize });
      };
      img.onerror = () => {
        if (isObjectURL) URL.revokeObjectURL(url);
        reject(new Error(`Load failed: ${file.name}`));
      };
      img.src = url;
    });
  }, [loadImageFromFile, processCanvas, getCurrentFilterValue, removeBackground, backgroundTolerance, outputFormat, cropEnabled, cropSides, autoScale, scaleFactor, jpegQualityValue]);

  const triggerDownload = useCallback((dataURL, filename) => {
    const link = document.createElement('a');
    link.href = dataURL;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  const handleConversion = useCallback(async () => {
    if (filesToConvert.length === 0) { showUserMessage('No files selected.', 'error'); return; }
    setIsConverting(true);
    setResults([]);
    setShowDownloadAll(false);
    setShowClearResults(false);
    setConversionProgress({ current: 0, total: filesToConvert.length, fileName: '' });
    showUserMessage(`Starting conversion for ${filesToConvert.length} image(s)...`, 'info', 0);

    let successCount = 0, errorCount = 0;
    const newResults = [];

    for (let i = 0; i < filesToConvert.length; i++) {
      const file = filesToConvert[i];
      setConversionProgress({ current: i + 1, total: filesToConvert.length, fileName: file.name });
      showUserMessage(`Converting ${i + 1}/${filesToConvert.length}: ${file.name}...`, 'info', 0);
      try {
        const result = await processAndConvertImage(file);
        newResults.push(result);
        if (currentMode === 'individual') triggerDownload(result.dataURL, result.newName);
        successCount++;
      } catch (error) {
        newResults.push({ originalName: file.name, error: error.message });
        errorCount++;
      }
    }

    setResults(newResults);
    if (successCount > 0 || errorCount > 0) setShowClearResults(true);
    if (successCount > 0 && currentMode === 'folder') setShowDownloadAll(true);
    setConversionProgress({ current: 0, total: 0, fileName: '' });
    showUserMessage(`Complete: ${successCount} successful, ${errorCount} failed.`, errorCount > 0 ? 'error' : 'success');
    setIsConverting(false);
  }, [filesToConvert, currentMode, processAndConvertImage, triggerDownload, showUserMessage]);

  const handleDownloadAll = useCallback(async () => {
    const zip = new JSZip();
    let filesInZip = 0;
    results.forEach(item => {
      if (item.dataURL && item.newName) {
        zip.file(item.newName, item.dataURL.split(',')[1], { base64: true });
        filesInZip++;
      }
    });

    if (filesInZip > 0) {
      showUserMessage('Generating ZIP...', 'info', 0);
      try {
        const content = await zip.generateAsync({ type: "blob" });
        const url = URL.createObjectURL(content);
        triggerDownload(url, `converted_images_${Date.now()}.zip`);
        setTimeout(() => URL.revokeObjectURL(url), 10000);
        showUserMessage('ZIP download started.', 'success');
      } catch (error) {
        showUserMessage(`ZIP failed: ${error.message}`, 'error');
      }
    } else {
      showUserMessage('No files to ZIP.', 'info');
    }
  }, [results, triggerDownload, showUserMessage]);

  const handleFileIndividualChange = useCallback((event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (isSupportedExtension(file.name)) {
        setFilesToConvert([file]);
        setFileInputIndividualText(`Selected: ${file.name}`);
        showUserMessage(`File "${file.name}" selected.`, 'info');
        updatePreview(file);
      } else {
        showUserMessage('Unsupported file type.', 'error');
        event.target.value = '';
        updatePreview(null);
        setFilesToConvert([]);
      }
    } else {
      setFileInputIndividualText('Drag & drop or click to select a file');
      updatePreview(null);
      setFilesToConvert([]);
    }
  }, [isSupportedExtension, showUserMessage, updatePreview]);

  const handleFolderInputChange = useCallback((event) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const selected = Array.from(files).filter(file => isSupportedExtension(file.name));
      if (selected.length > 0) {
        setFilesToConvert(selected);
        setFolderInputText(`${selected.length} image(s) found.`);
        showUserMessage(`${selected.length} images ready.`, 'info');
        updatePreview(selected[0]);
      } else {
        showUserMessage('No supported images found.', 'error');
        setFolderInputText('Drag & drop or click to select folder');
        updatePreview(null);
        setFilesToConvert([]);
      }
    } else {
      setFolderInputText('Drag & drop or click to select folder');
      updatePreview(null);
      setFilesToConvert([]);
    }
  }, [isSupportedExtension, showUserMessage, updatePreview]);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const droppedFiles = Array.from(event.dataTransfer.files);
    if (currentMode === 'individual') {
      const file = droppedFiles[0];
      if (file && isSupportedExtension(file.name)) {
        setFilesToConvert([file]);
        setFileInputIndividualText(`Selected: ${file.name}`);
        showUserMessage(`File "${file.name}" selected.`, 'info');
        updatePreview(file);
      } else {
        showUserMessage('Unsupported file type.', 'error');
      }
    } else {
      const supported = droppedFiles.filter(f => isSupportedExtension(f.name));
      if (supported.length > 0) {
        setFilesToConvert(supported);
        setFolderInputText(`${supported.length} image(s) found.`);
        showUserMessage(`${supported.length} images ready.`, 'info');
        updatePreview(supported[0]);
      } else {
        showUserMessage('No supported images found.', 'error');
      }
    }
  }, [currentMode, isSupportedExtension, showUserMessage, updatePreview]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return {
    // Refs
    fileInputIndividualRef, folderInputRef, previewCanvasRef,
    // State
    currentMode, setCurrentMode,
    filesToConvert, currentPreviewFile, previewVisible,
    isConverting, conversionProgress, message, setMessage,
    results, showDownloadAll, showClearResults,
    fileInputIndividualText, folderInputText,
    // Options
    outputFormat, setOutputFormat,
    jpegQualityValue, setJpegQualityValue,
    filtersEnabled, setFiltersEnabled,
    removeBackground, setRemoveBackground,
    backgroundTolerance, setBackgroundTolerance,
    cropEnabled, setCropEnabled,
    cropSides, updateCropSide,
    autoScale, setAutoScale,
    scaleFactor, setScaleFactor,
    filters, updateFilter, resetFilters,
    // Actions
    handleFileIndividualChange, handleFolderInputChange,
    handleConversion, handleDownloadAll, clearConverterOutputs,
    handleDrop, handleDragOver,
  };
}
